import { SalesClaims } from './auth-contracts';

/** Reads a JWT payload without verifying it: the API validates the signature, the UI only reads claims. */
export function decodeJwtPayload(token: string): SalesClaims | null {
  const parts = token.split('.');

  if (parts.length !== 3 || parts[1].length === 0) {
    return null;
  }

  try {
    const json = new TextDecoder().decode(base64UrlToBytes(parts[1]));
    const payload: unknown = JSON.parse(json);

    return payload !== null && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as SalesClaims)
      : null;
  } catch {
    return null;
  }
}

/** The token's `exp` as epoch milliseconds, or null. */
export function expiryOf(claims: SalesClaims): number | null {
  return typeof claims.exp === 'number' && Number.isFinite(claims.exp) ? claims.exp * 1000 : null;
}

/**
 * How long the server made the token last, from `exp - iat`, in milliseconds. Both claims come from the server's
 * clock, so the difference holds however far this PC's clock is off. Null when either claim is missing or not a
 * finite number, or `exp` is not after `iat`.
 */
export function lifetimeOf(claims: SalesClaims): number | null {
  const { iat, exp } = claims;

  if (typeof iat !== 'number' || !Number.isFinite(iat) || typeof exp !== 'number' || !Number.isFinite(exp)) {
    return null;
  }

  return exp > iat ? (exp - iat) * 1000 : null;
}

function base64UrlToBytes(segment: string): Uint8Array {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}
